/**
 * View-state persistence over the WorkspaceStore: the board reports its
 * viewport into the store (`reportViewport`), the focal path comes back as
 * the store's reactive slice, and this hook debounce-saves viewport +
 * selection + sidebar to localStorage.
 *
 * The split mirrors the store's design: the focal path is reactive state
 * (changes on dive/pop), the camera is read on demand via
 * `getLastViewport()` so a wheel-zoom never re-renders anything — only the
 * debounced save observes it. Selection lives in the store too, but is
 * passed in as props here so the save effect re-fires exactly when the
 * already-subscribed App re-renders (no second subscription).
 *
 * The navigation effects this sat next to in `App` — URL `?module=`/
 * `?process=` routing, deferred fly-to after a mode switch — stay in `App`:
 * each is cross-cutting orchestration over selection, mode, and sources,
 * not view-persistence state.
 */

import { useCallback, useEffect, useMemo, useRef } from "react";
import type { StableId } from "@archlang/scene/arch-model";
import type { Viewport } from "@archlang/scene/board";
import {
  savePersistedViewerState,
  debounce,
  type PersistedViewport,
} from "../viewerStorage";
import type { SidebarTab } from "../components/Sidebar";
import {
  useActiveTabId,
  useFocalPath,
  useTabs,
  type WorkspaceStore,
} from "../workbench/WorkspaceStore.js";

const persistDebounced = debounce(savePersistedViewerState, 200);

export interface ViewPersistenceInput {
  readonly selectedModuleId: StableId | null;
  readonly selectedEdgeId: string | null;
  readonly selectedInterfaceKey: string | null;
  readonly sidebarCollapsed: boolean;
  readonly sidebarTab: SidebarTab;
}

export interface ViewPersistenceController {
  readonly focalPath: readonly string[];
  readonly handleViewportChange: (vp: Viewport) => void;
}

export function useViewPersistence(
  store: WorkspaceStore,
  input: ViewPersistenceInput,
): ViewPersistenceController {
  const {
    selectedModuleId,
    selectedEdgeId,
    selectedInterfaceKey,
    sidebarCollapsed,
    sidebarTab,
  } = input;

  const focalPath = useFocalPath(store);
  // Subscribe so the persist effect re-fires on tab open/close/reorder/activate
  // (the tab snapshot is part of the persisted view state).
  const tabs = useTabs(store);
  const activeTabId = useActiveTabId(store);

  const persistNow = useCallback(() => {
    // Strip to the persisted shape — the live viewport may carry focalRects
    // (the dive/pop entry rects), which don't belong in localStorage.
    const vp = store.getLastViewport();
    const viewport: PersistedViewport | null = vp
      ? { focalPath: vp.focalPath, center: vp.center, zoom: vp.zoom }
      : null;
    persistDebounced({
      viewport,
      selectedModuleId: selectedModuleId ?? null,
      selectedEdgeId: selectedEdgeId ?? null,
      selectedInterfaceKey: selectedInterfaceKey ?? null,
      sidebarCollapsed,
      sidebarTab,
      // The LOGICAL tab set (visible + the single-tab stash), not `useTabs`'
      // collapsed list — persisting while the mobile collapse holds the
      // background tabs would destroy the desktop set on the next reload.
      // (`tabs` above stays a dep so tab changes re-fire this effect.)
      tabs: store.getLogicalTabs(),
      activeTabId,
      // The full per-tab view bundle (selection/aspect/process per tab) so each
      // tab reopens on exactly what it was looking at.
      views: store.getViews(),
    });
  }, [store, selectedModuleId, selectedEdgeId, selectedInterfaceKey, sidebarCollapsed, sidebarTab, tabs, activeTabId]);

  // Re-persist whenever selection / sidebar / tabs change (viewport unchanged).
  useEffect(() => {
    persistNow();
  }, [persistNow]);

  // Per-tab view changes (aspect colour, process scrubber, a board selection on
  // a tab the App doesn't surface via props) don't flow through the deps above —
  // subscribe so those persist too. The save is debounced, so a broad
  // subscription is cheap.
  useEffect(() => store.subscribe(persistNow), [store, persistNow]);

  // The viewport path runs at gesture rate (once per rAF while panning) —
  // building the persistence snapshot (incl. `getViews()`) per frame is pure
  // per-frame garbage since the save itself is debounced anyway. Debounce
  // snapshot-building too, reading the latest `persistNow` through a ref.
  const persistNowRef = useRef(persistNow);
  persistNowRef.current = persistNow;
  const persistLazy = useMemo(() => debounce(() => persistNowRef.current(), 200), []);

  const handleViewportChange = useCallback(
    (vp: Viewport) => {
      // Full object into the store (focalRects ride along for drill-down
      // restore exactness); the persisted copy is stripped above.
      store.reportViewport(vp);
      persistLazy();
    },
    [store, persistLazy],
  );

  return { focalPath, handleViewportChange };
}
